<script lang="ts">
  import type { OrderOption } from "../../models/order-options.interface";
  import { orderStore } from "../../services/order-options-service";
  import { fileStore } from "../../services/select-files";
  import DataTypeSelector from "./DataTypeSelector.svelte";
  import { onMount } from "svelte";
  import OptionRow from "./OptionRow.svelte";

  export let option: OrderOption;
  const id = "datatypes";
  let isDatTypesExpanded = false;
  let allDataTypes = [];

  // Set initial values on mount
  onMount(() => {
    // Aggregate and set all datatypes as selected in the orderStore
    const mergedDataTypes = mergeDataTypesFromFiles($fileStore.selectedFiles);

    if (mergedDataTypes.length > 0) {
      const orderValues = mergedDataTypes.map((dt) => ({
        id: dt.id,
        value: dt.name,
        selected: true
      }));

      orderStore.setOrderValue(id, orderValues);
    }
  });

  // Function to merge data types from all selected files
  function mergeDataTypesFromFiles(files) {
    const dataTypeMap = new Map();

    // Process each file's data types
    files.forEach((file, fileIndex) => {
      if (!file.dataTypes) {
        return;
      }

      file.dataTypes.forEach((dt) => {
        if (!dataTypeMap.has(dt.id)) {
          dataTypeMap.set(dt.id, {
            id: dt.id,
            name: dt.name,
            description: dt.description
          });
        }
      });
    });

    const result = Array.from(dataTypeMap.values());
    return result;
  }

  // Format the display summary of data types
  function formatDataTypeSummary(values): string {
    if (!values || values.length === 0) return "No data types selected";

    const selectedValues = values.filter((val) => val.selected);
    if (selectedValues.length === 0) return "No data types selected";

    const displayed = selectedValues.slice(0, 3).map((val) => val.value);

    if (selectedValues.length <= 3) {
      return displayed.join(", ");
    } else {
      return `${displayed.join(", ")}, ... (${selectedValues.length} total)`;
    }
  }

  // Update allDataTypes reactively
  $: {
    allDataTypes = mergeDataTypesFromFiles($fileStore.selectedFiles);
  }

  // Handle data type selection changes
  function handleDataTypesChange(event) {
    const { dataTypes } = event.detail;

    // Update order store
    const orderValues = dataTypes.map((dt) => ({
      id: dt.id,
      value: dt.name,
      selected: dt.selected
    }));

    orderStore.setOrderValue(id, orderValues);
  }
</script>

<OptionRow
  {id}
  label="Data Types"
  tooltip={option.params?.description ||
    "Data Types are the elements representing environmental observations."}
>
  {#if $orderStore.values[id]}
    {formatDataTypeSummary($orderStore.values[id])}
  {:else}
    No data types available
  {/if}

  <div class="mt-3">
    {#if !isDatTypesExpanded}
      <button
        class="btn btn-primary btn-sm px-3"
        on:click={() => (isDatTypesExpanded = !isDatTypesExpanded)}
      >
        <i class="bi bi-pencil me-1" /> Edit Data Types
      </button>
    {/if}

    {#if allDataTypes && allDataTypes.length > 0}
      <DataTypeSelector
        bind:expanded={isDatTypesExpanded}
        dataTypes={allDataTypes.map((dt) => ({
          ...dt,
          selected: $orderStore.values[id]?.some((val) => val.id === dt.id && val.selected) ?? true
        }))}
        on:change={handleDataTypesChange}
      />
    {/if}
  </div>
</OptionRow>
